<template>
    <div :id="rootElementId" :style="`height:${svgHeight }px`" class="radar-container"></div>
</template>

<script>
import * as d3v4 from "d3v4";
import { round,getDataUrl, getProductInfoById } from '../../utils/functions';
import { loadFonts,copyAndAppendElement } from '../../utils/exportToPNG';
import ResizeObserver from 'resize-observer-polyfill';
import tune from "@/assets/img/product-icons/tune.svg"; 
import speed from "@/assets/img/product-icons/speed.svg"; 
import faWandMagicSparkles from "@/assets/img/product-icons/fa-wand-magic-sparkles.svg"; 

export default {
  name: 'RadarChart',
  props: {
    size: {
      type: Number,
      required: false,
    },
    margin: {
      type: Object,
      required: false,
    },
    radarData: {
      type: Object,
      required: true,
    },
    dataPer90MinText: {
      type: String,
      required: false,
    },
    adjustToPossessionText: {
      type: String,
      required: false,
    },
    playerName: {
      type: String,
      required: true,
    },
    radarTitle: {
      type: String,
      required: true,
    },
    yob: {
      type: String,
      required: true,
    },
    comparePlayerData: {
      type: Object,
      required: false,
    },
    mediaMode:{
      type: Boolean,
      default: false
    },
    colors:{
      type: Object,
      required:false
    },
    bgColor:{
      type: String,
      required:false
    },
    textColor:{
      type: String,
      required:false
    },
    axisColor:{
      type: String,
      required:false
    },
    logoCfg:{
      type: String,
      required:false
    },
    id:{
      type: String,
      required: true
    },
    embed:{
      type: Boolean,
      default: false
    },
    fontFamily:{
      type: String,
      default: 'NeueMontreal'
    },
    showRadarIcons:{
      type: Boolean,
      default: false
    },
  },
  data() {
    return {
      observer: null,
      radarSize: 0
    }
  },
  computed: {
    svgWidth(){
      const radarScale = d3.scale.linear()
      .range([0.86, 0.92])
      .domain([650, 1000]);
      let SVG_WIDTH = this.radarSize * 0.85 > 350 || this.embed ? this.radarSize * radarScale(this.radarSize): 350;
      SVG_WIDTH = this.embed ? this.radarSize : SVG_WIDTH;
      return SVG_WIDTH;
    },
    svgHeight() {
      const isMobileDevice = this.svgWidth < 440 ? 1 : 0;
      let SVG_HEIGHT = isMobileDevice ? this.svgWidth * 0.9 : this.svgWidth * .80;
      SVG_HEIGHT = this.embed ? this.radarSize * 0.8 : SVG_HEIGHT;
      return SVG_HEIGHT;
    },
    darkMode(){
      return this.$vuetify.theme.dark;
    },
    rootElementId(){
      return `radar${this.id}`
    },
    currentUserLanguage() {
      return this.$store.getters["auth/language"];
    },
  },
  async mounted() {
    //Draw RadarChart
    await this.drawRadarChart('#' + this.rootElementId, this.radarData, this.comparePlayerData, this.size, this.margin);
  },
  methods: {
    getStatIcon(stat) {
      switch(stat.product.icon) {
        case 'tune':
          return tune;
        case 'speed':
          return speed;
        case 'fa-wand-magic-sparkles':
          return faWandMagicSparkles;
        default:
          return '';
      };
    },
    async drawRadarChart(id, radarData, comparePlayerRadarData, size, margin) {
    
      this.radarSize = size;
      this.observer = new ResizeObserver(this.onResize).observe(document.getElementById(this.rootElementId));
      let strokes = "linear-closed";
      let isRadarComparison = !!radarData.statDTOs[1];
      let playerRadarData = radarData.statDTOs[0];
      const numberOfRadarStats = radarData.statDTOs[0].length;
      //0.7s duration transition between old radar and neew radar
      const duration = 700;
      
      let SVG_WIDTH = this.svgWidth;
      const isMobileDevice = SVG_WIDTH < 440 ? 1 : 0;

      const SVG_HEIGHT = this.svgHeight;

      const heightRect = 43;
      const CHART_SIZE = SVG_WIDTH / 2;
      const CHART_SIZE_HEIGHT = SVG_HEIGHT / 2;
      const ratio = SVG_WIDTH <= 380 ? 0.002 : SVG_WIDTH < 700 ? 0.0017 : 0.00135;
      const CHART_SIZE_RATIO = CHART_SIZE * ratio;

      //Over this value, valueToShow format to just one decimal
      const VALUE_OVER_WHICH_JUST_ONE_DECIMAL = 10;
      let radarWidth = SVG_WIDTH - margin.right * 2;

      //If this field increase, svg width increase (Don't affect to radar width)
      const RADAR_WIDTH = radarWidth > 230 || this.embed ? radarWidth : 230;
      const RADAR_HEIGHT = RADAR_WIDTH;

      var cfg = {
       levels: 4,				//How many levels or inner circles should there be drawn
       labelFactor: this.$vuetify.breakpoint.xlOnly ? 1.15 : 1.2, 	//How much farther than the radius of the outer circle should the labels be placed
       wrapWidth: this.$vuetify.breakpoint.xlOnly ? 120 : 90, //The number of pixels after which a label needs to be given a new line
       opacityArea: 0.5, 	//The opacity of the area of the blob
       dotRadius: 4.5, 			//The size of the colored circles of each blog
       opacityCircles: 0.1, 	//The opacity of the circles of each blob
       strokeWidth: 1.7, 		//The width of the stroke around each blob
       outlineStrokeWidth: 1,
       labelFontSize: 20, //Font size of the labels
       tooltipFontSize: 24, //Font size of the tooltip
       innerCircleFontSize: 16,		//Font size of the labels
       roundStrokes: true,	//If true the area and stroke will follow a round path (cardinal-closed)
       color: [this.colors.fillColor, this.colors.comparisonFillColor],	//Color function
       borderColor: [this.colors.borderColor, this.colors.comparisonBorderColor],	//Color function
       iconOffsetX: this.showRadarIcons ? 6 : 0, //X offset in case stat icons are printed
      };
      //If the supplied maxValue is smaller than the actual one, replace by the max in the data
      var maxValue = 1;
      //Names of each axis
      var allAxis = (playerRadarData.map((stat, index) => {
        let statValue =  round(stat.valueToShow, stat.valueToShow > VALUE_OVER_WHICH_JUST_ONE_DECIMAL ? 1 : 2);
        //If value > 0
        if(statValue){
          statValue += stat.unitCode ? " " + stat.unitCode : "";
          statValue += stat.isPercentage ? "%" : "";
        }else if(stat.statTypeId == 3){
          //If value is 0 and is a Ratio Stat (statTypeId = 3)
          statValue = "-";
        }

        return {
          label: (stat.isAdjustedToPossession ? "*" : "") + this.$t('statsNaming.' + stat.code),
          value: statValue,
          tooltip: this.$t('statsNaming.description.' + stat.code),
          product: getProductInfoById(stat.productId, this.currentUserLanguage)
        }
      })),
        minMaxValues = (playerRadarData.map(function(stat, index){ return {min: stat.minValue, max: stat.maxValue};})),	//Min and Max Values of each stat
        total = allAxis.length,					//The number of different axes
        radius = Math.min(RADAR_WIDTH/2, RADAR_HEIGHT/2), 	//Radius of the outermost circle
        angleSlice = Math.PI * 2 / total; //The width in radians of each "slice"

      // If comparison radar, append values of second player to axis text
  		if (isRadarComparison) {
        //Second radar [1] is comparison radar
  			radarData.statDTOs[1].forEach(function(stat, index) {
          //Get value between min max limits
          let min = playerRadarData[index].minValue;
          let max = playerRadarData[index].maxValue;
          let v = stat.valueToShow;
          if(max > min){
            v = v < min ? min : v > max ? max : v;
          }else{
            v = v < max ? max : v > min ? min : v;
          }
          //Update min and max value of compare radar with minMax values of main radar
          stat.minValue = min;
          stat.maxValue = max;
          stat.value = round(v, v > VALUE_OVER_WHICH_JUST_ONE_DECIMAL ? 1 : 2);

          let statValue =  round(stat.valueToShow, stat.valueToShow > VALUE_OVER_WHICH_JUST_ONE_DECIMAL ? 1 : 2);
          //If value > 0
          if(statValue){
            statValue += stat.unitCode ? " " + stat.unitCode : "";
            statValue += stat.isPercentage ? "%" : "";
          }else if(stat.statTypeId == 3){
            //If value is 0 and is a Ratio Stat (statTypeId = 3), set - as value to show
            statValue = "-";
          }
  				allAxis[index].comparisonValue = statValue;
  			});
  		}
      //Scale for the radius
      var rScale = d3.scale.linear()
        .range([0, radius])
        .domain([0, maxValue]);

      ///////////////////////////////////////////////////////////////////
      //////////// Create the container SVG, g and tooltip /////////////
      /////////////////////////////////////////////////////////////////

      //Recover lines and dots from previous SVG
      var prevSVG = d3.select(id).select('svg'),
      circles = [],
      lines = [];
      if (prevSVG.node()) {
        prevSVG.selectAll(".radarCircle").each(function(){
          circles.push([d3.select(this).attr("cx"), d3.select(this).attr("cy")]);
        });
        if(isRadarComparison){
          prevSVG.selectAll(".radarCircleCompare").each(function(){
            circles.push([d3.select(this).attr("cx"), d3.select(this).attr("cy")]);
          });
        }
        prevSVG.selectAll(".radarStroke").each(function(){
          lines.push(d3.select(this).attr("d"));
        });
        if(isRadarComparison){
          prevSVG.selectAll(".radarStrokeCompare").each(function(){
            lines.push(d3.select(this).attr("d"));
          });
        }
        //Remove previous SVG
        prevSVG.remove();
      }

      //Initiate the radar chart SVG
      var svg = d3.select(id).append("svg");
      //Append a g element
      var g = svg
        .attr("width",  this.svgWidth)
        .attr("height", this.svgHeight)
        .attr("class", "radar")
        .style("fill", this.textColor)
        .style("background", this.bgColor)
        .attr("id",`${this.rootElementId}SVG`)
        .append("g")
        .attr("transform", "translate(" + (CHART_SIZE) + "," + CHART_SIZE_HEIGHT * ( isMobileDevice ? 1 : 1.05 )+ ")");

      // Define the div for the tooltip
      var tooltip = d3v4.select("body").append("div")
      .attr("class", "tooltip")
      .style("z-index", "9999")
      .style("display", "none");

      ////////////////////////////////////
      ////////// Glow filter  ///////////
      //////////////////////////////////

      //Filter for the outside glow
      var filter = g.append('defs').append('filter').attr('id','glow'),
        feGaussianBlur = filter.append('feGaussianBlur').attr('stdDeviation', 2.5 * CHART_SIZE_RATIO).attr('result','coloredBlur'),
        feMerge = filter.append('feMerge'),
        feMergeNode_1 = feMerge.append('feMergeNode').attr('in','coloredBlur'),
        feMergeNode_2 = feMerge.append('feMergeNode').attr('in','SourceGraphic');

      /////////////////////////////////////////////////////////
      /////////////// Draw the Circular grid //////////////////
      /////////////////////////////////////////////////////////

      //Wrapper for the grid & axes
      var axisGrid = g.append("g").attr("class", "axisWrapper");

      //Draw the background circles
      axisGrid.selectAll(".levels")
        .data(d3.range(1,(cfg.levels+1)).reverse())
        .enter()
        .append("circle")
        .attr("class", "gridCircle")
        .attr("r", function(d, i){ return radius/cfg.levels * d;})
        .style("fill", "#CDCDCD")
        .style("stroke", function(d,i) { return i % 2 === 0 ? "#9C9C9C" : "#CDCDCD" })
        .style("stroke-width", cfg.outlineStrokeWidth * CHART_SIZE_RATIO)
        .style("fill-opacity", cfg.opacityCircles)
        .style("filter" , "url(#glow)");

        // TITLES
    	if (isRadarComparison) {
        //Draw Blue rectangle
    		axisGrid.append("rect")
    			.attr("x", - (SVG_WIDTH/2) + 1)
    			.attr("y", - (RADAR_HEIGHT / 2 + margin.top/2 + 9))
    			.attr("width", 20 )
    			.attr("height", heightRect * (isMobileDevice ? 0.8 : 1))
    			.attr("text-anchor", "left")
          .attr('stroke', cfg.borderColor[0])
          .attr('stroke-width', (3 * CHART_SIZE_RATIO))
          .style("fill", cfg.color[0]);

        //Draw Black rectangle
    		axisGrid.append("rect")
    			.attr("x", (SVG_WIDTH/2) - 25)
    			.attr("y", -(RADAR_HEIGHT / 2 + margin.top/2 + 9))
    			.attr("width", 20)
    			.attr("height", heightRect * (isMobileDevice ? 0.8 : 1))
    			.attr("text-anchor", "right")
          .attr('stroke', cfg.borderColor[1])
          .attr('stroke-width', (3 * CHART_SIZE_RATIO))
          .style("fill", cfg.color[1]);

    		let mainPlayerNameText = axisGrid.append("text")
    			.attr("x", - (SVG_WIDTH/2) + 25)
    			.attr("y", - (RADAR_HEIGHT / 2 + margin.top / 2) + (isMobileDevice ? -1 : 2))
    			.attr("text-anchor", "left")
    			.style("font-size", (7 * CHART_SIZE_RATIO) + "px")
    			.style("font-family", this.fontFamily)
    			.style("font-weight", "bold")
          .style("color", this.textColor)
          .style("fill",'currentColor')
    			.text(this.playerName);

        //Do transition for Main Player Name
        mainPlayerNameText.transition().duration(duration).style("font-size", (32 * CHART_SIZE_RATIO) + "px");
        //Add YOB to main player
        let titleYOBMainPlayer = mainPlayerNameText.append("tspan")
    			.attr("dx", 0)
    			.attr("dy", -(2 * CHART_SIZE_RATIO))
    			.style("font-size", (7 * CHART_SIZE_RATIO) + "px")
    			.style("font-weight", "normal")
    			.text(this.yob ? " ('" + this.yob + ")" : "");

        //Do transition for Year of Birth of Main Player
        titleYOBMainPlayer.transition().duration(duration).style("font-size", (24 * CHART_SIZE_RATIO) + "px");

    		let titleYOBComparePlayer = axisGrid.append("text")
          .attr("x", (SVG_WIDTH/2) - 30)
          .attr("y", - (RADAR_HEIGHT / 2 + margin.top / 2) + (isMobileDevice ? -1 : 2))
    			.attr("text-anchor", "end")
    			.style("font-size", (7 * CHART_SIZE_RATIO) +  "px")
    			.style("font-family", this.fontFamily)
    			.style("font-weight", "normal")
          .style("color", this.textColor)
          .style("fill",'currentColor')
    			.text(comparePlayerRadarData.yob ? "('" + comparePlayerRadarData.yob + ") " : "");

        //Do transition for Year of Birth of Compare Player
        titleYOBComparePlayer.transition().duration(duration).style("font-size", (24 * CHART_SIZE_RATIO) + "px");
        //Add Name to compare player
        let comparePlayerNameText = titleYOBComparePlayer.append("tspan")
    			.attr("dx", 0)
    			.attr("dy", (2 * CHART_SIZE_RATIO))
    			.style("font-size", (7 * CHART_SIZE_RATIO) + "px")
    			.style("font-weight", "bold")
    			.text(comparePlayerRadarData.playerName);

        //Do transition for Compare Player Name
        comparePlayerNameText.transition().duration(duration).style("font-size", (32 * CHART_SIZE_RATIO) + "px");

        //Write Team and Season (or Seasons) text
        let teamAndSeasontext = (isMobileDevice && this.radarTitle.length > 37) ? this.radarTitle.substring(0, 37) + "..." : this.radarTitle;
    		let mainPlayerSeasonText = axisGrid.append("text")
    			.attr("x", - (SVG_WIDTH/2) + 25)
    			.attr("y", - (RADAR_HEIGHT / 2 + margin.top / 2) + 18 * (isMobileDevice ? 0.58 : 1))
    			.attr("text-anchor", "left")
    			.style("font-size", (7 * CHART_SIZE_RATIO)  + "px")
          .style("font-family", this.fontFamily)
          .style("color", this.textColor)
          .style("fill",'currentColor')
    			.text(teamAndSeasontext);

        //Do transition for Main Player Season Text
        mainPlayerSeasonText.transition().duration(duration).style("font-size", (18 * CHART_SIZE_RATIO) + "px");

    		let comparePlayerSeasonText = axisGrid.append("text")
    			.attr("x", (SVG_WIDTH/2) - 30)
    			.attr("y", -(RADAR_HEIGHT / 2 + margin.top/2) + 18 * (isMobileDevice ? 0.58 : 1))
    			.attr("text-anchor", "end")
    			.style("font-size", (7 * CHART_SIZE_RATIO) + "px")
    			.style("font-family", this.fontFamily)
          .style("color", this.textColor)
          .style("fill",'currentColor')
    			.text(comparePlayerRadarData.seasonsName);

        //Do transition for Main Player Season Text
        comparePlayerSeasonText.transition().duration(duration).style("font-size", (18 * CHART_SIZE_RATIO) + "px");

  			//Just Print minutes Played for Player Comparison Radar
        let minsPlayedMainPlayer = radarData.minutesPlayed ? radarData.minutesPlayed + " mins." : "";
  			let mainPlayerMinsPlayedText = axisGrid.append("text")
  				.attr("x", - (SVG_WIDTH/2) + 25)
  				.attr("y", - (RADAR_HEIGHT / 2 + margin.top / 2) + 32 * (isMobileDevice ? 0.65 : 1))
  				.attr("text-anchor", "left")
  				.style("font-size", (7 * CHART_SIZE_RATIO)  + "px")
  				.style("font-family", this.fontFamily)
          .style("color", this.textColor)
          .style("fill",'currentColor')
  				.text(minsPlayedMainPlayer);

        //Do transition for Main Player Mins Played Text
        mainPlayerMinsPlayedText.transition().duration(duration).style("font-size", (18 * CHART_SIZE_RATIO) + "px");

        let minsPlayedComparePlayer = comparePlayerRadarData.minsPlayed + " mins.";
        if(comparePlayerRadarData.currentMatchDay){
          minsPlayedComparePlayer = comparePlayerRadarData.currentMatchDay + " " + this.$t('playerRecruitment.radarDescription.basicStatistics.games');
        }
  			let comparePlayerMinsPlayedText = axisGrid.append("text")
  				.attr("x", (SVG_WIDTH/2) - 30)
  				.attr("y", -(RADAR_HEIGHT / 2 + margin.top/2) + 32 * (isMobileDevice ? 0.65 : 1))
  				.attr("text-anchor", "end")
  				.style("font-size", (7 * CHART_SIZE_RATIO) + "px")
  				.style("font-family", this.fontFamily)
          .style("color", this.textColor)
          .style("fill",'currentColor')
  				.text(minsPlayedComparePlayer);

        //Do transition for Main Player Mins Played Text
        comparePlayerMinsPlayedText.transition().duration(duration).style("font-size", (18 * CHART_SIZE_RATIO) + "px");

    	} else {

        let titleText = svg.append("text")
          .attr("x", SVG_WIDTH / 2)
          .attr("y", 25 * CHART_SIZE_RATIO)
          .attr("text-anchor", "middle")
          .style("font-size", (7 * CHART_SIZE_RATIO) + "px")
          .style("font-family", this.fontFamily)
          .style("font-weight", "bold")
          .style("color", this.textColor)
          .style("fill",'currentColor')
          .text(this.playerName);
        //Do transition for PlayerName
        titleText.transition().duration(duration).style("font-size", (32 * CHART_SIZE_RATIO) + "px");
        //Add Year of Birth to playerName
        if(this.yob){
          let titleYOB = titleText.append("tspan")
            .attr("dx", 0)
            .attr("dy", -(2 * CHART_SIZE_RATIO))
            .style("font-size", (7 * CHART_SIZE_RATIO) + "px")
            .style("font-weight", "normal")
            .text(" ('" + this.yob + ")"); //Last 2 digits of Player's Year of Birth

          //Do transition for Year of Birth
          titleYOB.transition().duration(duration).style("font-size", (24 * CHART_SIZE_RATIO) + "px");
        }

        //Write Team and Season (or Seasons) text
        let seasonText = svg.append("text")
          .attr("x", SVG_WIDTH / 2)
          .attr("y", 55 * CHART_SIZE_RATIO)
          .attr("text-anchor", "middle")
          .style("font-size", (7 * CHART_SIZE_RATIO) + "px")
          .style("font-family", this.fontFamily)
          .style("color",this.textColor)
          .style("fill",'currentColor')
          .html(this.radarTitle + " · " + radarData.minutesPlayed + " mins.");

        //Do transition for Season Text
        seasonText.transition().duration(duration).style("font-size", (24 * CHART_SIZE_RATIO) + "px");
      }

      // Draw Left Text
      svg.append("text")
        .attr("x", 0)
        .attr("y", SVG_HEIGHT - 70 * CHART_SIZE_RATIO)
        .attr("text-anchor", "start")
        .attr("dy", "0.8em")
        .style("font-size", (24 * CHART_SIZE_RATIO) + "px")
        .style("font-family", this.fontFamily)
        .style("color",this.textColor)
        .style("fill",'currentColor')
        .text(this.dataPer90MinText);

      svg.append("text")
        .attr("x", 0)
        .attr("y", SVG_HEIGHT - 35 * CHART_SIZE_RATIO)
        .attr("text-anchor", "start")
        .attr("dy", "0.8em")
        .style("font-size", (24 * CHART_SIZE_RATIO) + "px")
        .style("font-family", this.fontFamily)
        .style("color", this.textColor)
        .style("fill",'currentColor')
        .text(this.adjustToPossessionText);


      // Draw Driblab Logo
      const logoWidth = SVG_WIDTH * 0.22;
      const logoHeight = SVG_HEIGHT * 0.1;

      if(!this.mediaMode){
        let base_image = new Image();
        base_image.src = this.mediaMode ? require(this.logoCfg === 'white' ? "@/assets/img/driblab_normal_white.png" : "@/assets/img/driblab_normal_color.png") :
         require((this.logoCfg === 'white' || this.darkMode) ? "@/assets/img/driblab_white.png" : "@/assets/img/driblab_color.png");
        base_image.onload = (event) => {
          const dataUrl = getDataUrl(event.currentTarget);
          this.drawImage(
          svg,
          "driblab-logo",
          SVG_WIDTH - logoWidth,
          SVG_HEIGHT - logoHeight,
          logoWidth,
          logoHeight,
          dataUrl
          );
        }
      }

      var self = this;
      /////////////////////////////////////////////////////////
      //////////////////// Draw the axes //////////////////////
      /////////////////////////////////////////////////////////

      //Create the straight lines radiating outward from the center
      var axis = axisGrid.selectAll(".axis")
        .data(allAxis)
        .enter()
        .append("g")
        .attr("class", "axis");
      //Append the lines
      axis.append("line")
        .attr("x1", 0)
        .attr("y1", 0)
        .attr("x2", function(d, i){ return rScale(maxValue) * Math.cos(angleSlice*i - Math.PI/2); })
        .attr("y2", function(d, i){ return rScale(maxValue) * Math.sin(angleSlice*i - Math.PI/2); })
        .attr("class", "line")
        .style("stroke", this.axisColor)
        .style("stroke-width", (2 * CHART_SIZE_RATIO) + "px");

      //Append the labels at each axis
    	if (isRadarComparison) {
        //Comparison Radar. Print numbers with colors
        axis.append("text")
          .style("color", this.textColor)
          .style("fill",'currentColor')
          .style("font-size", (CHART_SIZE_RATIO * cfg.labelFontSize) + "px")
          .style("font-family", this.fontFamily)
          .attr("text-anchor", "middle")
          .attr("dy", "0.6em")
          .attr("x", function(d, i){
            var coefX = isMobileDevice ? 1.11 : 1.13;
            return rScale(maxValue * coefX * cfg.labelFactor) * Math.cos(angleSlice * i - Math.PI / 2) + cfg.iconOffsetX; //+6 to center label with icon
          })
          .attr("y", function(d, i){
            var coefY = 0.93;
            if (i === 0) // top label
              coefY = 0.98;
            else if (i === 1 || i === total - 1 && (d.label.length + d.value.length) > 15) // labels next to top label, a bit up
              coefY = 1.01;
            else if (total % 2 === 0 && i === total/2) // bottom label
              coefY = 0.9;
            return (rScale(maxValue * coefY * cfg.labelFactor) * Math.sin(angleSlice*i - Math.PI/2))
           })
           .text(function(d){return d.label})
           .each(function(d, i) {
              const textElement = d3.select(this);
              // Delay to allow wrapping to complete
              setTimeout(() => {
                  const firstTspan = textElement.selectAll("tspan").node(); // Get first <tspan> element
                  if (firstTspan) {
                    // getComputedTextLength() give me length of text in pixels
                    const firstLineWidth = firstTspan.getComputedTextLength();
                    const textX = textElement.attr("x");
                    let coordX = textX - (firstLineWidth / 2) - 18;
                    // Append image just before text
                    if (self.showRadarIcons) {
                      axis.append("image")
                        .attr("xlink:href", self.getStatIcon(d))
                        .attr("width", 16)
                        .attr("height", 16)
                        .attr("x", coordX) 
                        .attr("y", d3.select(this).attr("y") - 4); // Center vertically
                    };
                  }
              }, 10);
            })
           .on('mouseover',function(stat, i) {
              let widthTooltip = 350;
              let lines = Math.ceil(self.measureText(stat.tooltip) / widthTooltip);
              const statName = stat.label;
              const statProduct = stat.product ? stat.product.name : '';

              let heightTooltip = (CHART_SIZE * 0.2 + (isMobileDevice * 2.5)) + (lines * 10);
              if(((widthTooltip / 2) + this.getBoundingClientRect().left) >= window.innerWidth){
               widthTooltip = (window.innerWidth - this.getBoundingClientRect().left) * 1.75;
              }else if( (this.getBoundingClientRect().left - (widthTooltip / 2))  <= 0){
               widthTooltip = this.getBoundingClientRect().left * 1.75;
              }

              let tooltipY = event.pageY - (heightTooltip + (isMobileDevice * 5));

              if(tooltipY < 0){
               //Hide bottom arrow
               d3.select("body > .tooltip").attr("class",'tooltip no-arrow');
               tooltipY = event.pageY + 8;
              }else{
               //Set bottom arrow
               d3.select("body > .tooltip").attr("class",'tooltip');
              }

              d3.select("body > .tooltip")
                .transition()
                .duration(200)
                .style("display", "inline-block")
                .style("opacity", 0.95)
                .style("width", widthTooltip + "px")
                .style("height", "auto");
             
              d3.select("body > .tooltip").html("<b>" + statName + "</b></br>" + statProduct + "</br>" + stat.tooltip)
                .style("font-size", (cfg.tooltipFontSize * CHART_SIZE_RATIO) + "px")
                .style("padding-top", (10 * CHART_SIZE_RATIO) + "px")
                .style("left", this.getBoundingClientRect().right - (this.getBoundingClientRect().width/2) - widthTooltip/2 + "px")
                .style("top", tooltipY + "px");

            })
            .on('mouseout',function() {
              d3.select("body > .tooltip").transition()
                .duration(300)
                .style("display", "none");
            })
           .call(this.wrap,  isMobileDevice ? 0.6 * cfg.wrapWidth : cfg.wrapWidth, cfg.iconOffsetX)
           .append("tspan")
           .attr("x", function(d, i){
              var coefX = isMobileDevice ? 1.11 : 1.13;
              return rScale(maxValue * coefX * cfg.labelFactor) * Math.cos(angleSlice * i - Math.PI / 2);
           })
           .attr("dy", "1.3em")
           .attr("fill", this.darkMode ? cfg.color[0] : cfg.borderColor[0])
           .style("font-weight", "bold")
           .text(function(d){return d.value})
           .append("tspan")
           .attr("fill", this.darkMode ? "#FFFFFF" : this.textColor)
           .style("font-weight", "normal")
           .text(" vs ")
           .append("tspan")
           .attr("fill", this.darkMode ? "#FFFFFF" : this.textColor)
           .style("font-weight", "bold")
           .text(function(d){ return d.comparisonValue })
      }else{
        //Normal Radar
        axis.append("text")
          .style("color", this.textColor)
          .style("fill",'currentColor')
          .style("font-size", (CHART_SIZE_RATIO * cfg.labelFontSize) + "px")
          .style("font-family", this.fontFamily)
          .attr("text-anchor", "middle")
          .attr("dy", "0.6em")
          .attr("x", function(d, i){
            var coefX = isMobileDevice ? 1.09 : 1.13;
            return rScale(maxValue * coefX * cfg.labelFactor) * Math.cos(angleSlice * i - Math.PI / 2) + cfg.iconOffsetX; //+6 to center label with icon
          })
          .attr("y", function(d, i){
            var coefY = 0.93;
            // top label
            if (i === 0)
              coefY = d.label.length > 15 ? 1 : 0.96;
            // labels next to top label, a bit up
            else if (i <= 2 || i === total - 1 && (d.label.length + d.value.length) > 15)
              coefY = 1.01;
            // bottom label
            else if (total % 2 === 0 && i === total/2)
              coefY = 0.9;

            return (rScale(maxValue * coefY * cfg.labelFactor) * Math.sin(angleSlice*i - Math.PI/2))
           })
           .text(function(d){ return d.label })
           .each(function(d, i) {
              const textElement = d3.select(this);
              // Delay to allow wrapping to complete
              setTimeout(() => {
                  const firstTspan = textElement.selectAll("tspan").node(); // Get first <tspan> element
                  if (firstTspan) {
                    // getComputedTextLength() give me length of text in pixels
                    const firstLineWidth = firstTspan.getComputedTextLength();
                    const textX = textElement.attr("x");
                    let coordX = textX - (firstLineWidth / 2) - 18;
                    // Append image just before text
                    if (self.showRadarIcons) {
                      axis.append("image")
                        .attr("xlink:href", self.getStatIcon(d))
                        .attr("width", 16)
                        .attr("height", 16)
                        .attr("x", coordX) 
                        .attr("y", d3.select(this).attr("y") - 4); // Center vertically
                    };
                  }
              }, 10);
            })
           .on('mouseover',function(stat, i) {
              let widthTooltip = 350;
              let lines = Math.ceil(self.measureText(stat.tooltip) / widthTooltip);
              const statName = stat.label;
              const statProduct = stat.product ? stat.product.name : '';

              let heightTooltip = (CHART_SIZE * 0.2 + (isMobileDevice * 2.5)) + (lines * 10);
              if(((widthTooltip / 2) + this.getBoundingClientRect().left) >= window.innerWidth){
                widthTooltip = (window.innerWidth - this.getBoundingClientRect().left) * 1.75;
              }else if( (this.getBoundingClientRect().left - (widthTooltip / 2))  <= 0){
                widthTooltip = this.getBoundingClientRect().left * 1.75;
              }
              let tooltipY = event.pageY - (heightTooltip + (isMobileDevice * 5));

              if(tooltipY < 0){
                //Hide bottom arrow
                d3.select("body > .tooltip").attr("class",'tooltip no-arrow');
                tooltipY = event.pageY + 8;
              }else{
                //Set bottom arrow
                d3.select("body > .tooltip").attr("class",'tooltip');
              }

              d3.select("body > .tooltip")
                .transition()
                .duration(200)
                .style("display", "inline-block")
                .style("opacity", 0.95)
                .style("width", widthTooltip + "px")
                .style("height", "auto");
 
              d3.select("body > .tooltip").html("<b>" + statName + "</b></br>" + statProduct + "</br>" + stat.tooltip)
                .style("font-size", (cfg.tooltipFontSize * CHART_SIZE_RATIO) + "px")
                .style("padding-top", (10 * CHART_SIZE_RATIO) + "px")
                .style("left", this.getBoundingClientRect().right - (this.getBoundingClientRect().width/2) - widthTooltip/2 + "px")
                .style("top", tooltipY + "px");
            })
            .on('mouseout',function() {
              d3.select("body > .tooltip").transition()
                .duration(300)
                .style("display", "none");
            })
           .call(this.wrap,  isMobileDevice ? 0.6 * cfg.wrapWidth : cfg.wrapWidth, cfg.iconOffsetX)
           .append("tspan")
           .attr("x", function(d, i){
              var coefX = isMobileDevice ? 1.09 : 1.13;
              return rScale(maxValue * coefX * cfg.labelFactor) * Math.cos(angleSlice * i - Math.PI / 2);
           })
           .attr("dy", "1.3em")
           .attr("fill", this.darkMode ? cfg.color[0] : cfg.borderColor[0])
           .style("font-weight", "bold")
           .text(function(d){ return d.value });
      }

      var values = axisGrid.selectAll(".maxValues")
        .data(minMaxValues)
        .enter()
        .append("g")
        .attr("class", "maxValues");
       values.append("text")
           .attr("class", "axisLabel")
           .style("font-size", (CHART_SIZE_RATIO * cfg.innerCircleFontSize) - (isMobileDevice * CHART_SIZE_RATIO) + "px")
           .style("font-family", this.fontFamily)
           .style("color", this.textColor)
           .style("fill",'currentColor')
           .attr("text-anchor", "middle")
           .attr("dy", "0.6em")
           .attr("x", function(d, i){ return 0.5 * rScale(maxValue * 0.84 * cfg.labelFactor) * Math.cos(angleSlice*i - Math.PI/2); })
           .attr("y", function(d, i){ return 0.5 * rScale(maxValue * 0.84 * cfg.labelFactor) * Math.sin(angleSlice*i - Math.PI/2); })
           .text(function(d){ return d.max > 5 ? round(d.min + (0.5 * (d.max - d.min)),1) : round(d.min + (0.5 * (d.max - d.min)),2)})
           .call(this.wrap, cfg.wrapWidth, cfg.iconOffsetX);
       values.append("text")
           .attr("class", "axisLabel")
           .style("font-size", (CHART_SIZE_RATIO * cfg.innerCircleFontSize) - (isMobileDevice * CHART_SIZE_RATIO) + "px")
           .style("font-family", this.fontFamily)
           .style("color", this.textColor)
           .style("fill",'currentColor')
           .attr("text-anchor", "middle")
           .attr("dy", "0.6em")
           .attr("x", function(d, i){ return 0.75 * rScale(maxValue * 0.84 * cfg.labelFactor) * Math.cos(angleSlice*i - Math.PI/2); })
           .attr("y", function(d, i){ return 0.75 * rScale(maxValue * 0.84 * cfg.labelFactor) * Math.sin(angleSlice*i - Math.PI/2); })
           .text(function(d){ return d.max > 5 ? round(d.min + (0.75 * (d.max - d.min)),1) : round(d.min + (0.75 * (d.max - d.min)),2)})
           .call(this.wrap, cfg.wrapWidth, cfg.iconOffsetX);
       values.append("text")
           .attr("class", "axisLabel")
           .style("font-size", (CHART_SIZE_RATIO * cfg.innerCircleFontSize) - (isMobileDevice * CHART_SIZE_RATIO) + "px")
           .style("font-family", this.fontFamily)
           .style("color", this.textColor)
           .style("fill",'currentColor')
           .attr("text-anchor", "middle")
           .attr("dy", "0.6em")
           .attr("x", function(d, i){ return 0.25 * rScale(maxValue * 0.84 * cfg.labelFactor) * Math.cos(angleSlice*i - Math.PI/2); })
           .attr("y", function(d, i){ return 0.25 * rScale(maxValue * 0.84 * cfg.labelFactor) * Math.sin(angleSlice*i - Math.PI/2); })
           .text(function(d){ return d.max > 5 ? round(d.min + (0.25 * (d.max - d.min)),1) : round(d.min + (0.25 * (d.max - d.min)),2)})
           .call(this.wrap, cfg.wrapWidth, cfg.iconOffsetX);
        values.append("text")
          .attr("class", "axisLabel")
          .style("font-size", (CHART_SIZE_RATIO * cfg.innerCircleFontSize) - (isMobileDevice * CHART_SIZE_RATIO) + "px")
          .style("font-family", this.fontFamily)
          .style("color", this.textColor)
          .style("fill",'currentColor')
          .attr("text-anchor", "middle")
          .attr("dy", "0.6em")
          .attr("x", function(d, i){ return rScale(maxValue * 0.84 * cfg.labelFactor) * Math.cos(angleSlice*i - Math.PI/2); })
          .attr("y", function(d, i){ return rScale(maxValue * 0.84 * cfg.labelFactor) * Math.sin(angleSlice*i - Math.PI/2); })
          .text(function(d){ return d.max > 5 ? round(d.max,1) : round(d.max, 2)})
          .call(this.wrap, cfg.wrapWidth, cfg.iconOffsetX);

      /////////////////////////////////////////////////////////
      ///////////// Draw the radar chart blobs ////////////////
      /////////////////////////////////////////////////////////

      //The radial line function
      var radarLine = d3.svg.line.radial()
        .interpolate(strokes)
        .radius(function(d) { return rScale((d.value - d.minValue) / (d.maxValue - d.minValue)); })
        .angle(function(d,i) {	return i*angleSlice; });

      if(cfg.roundStrokes) {
        radarLine.interpolate(strokes);
      }

      //Create a wrapper for the blobs
      var blobWrapper = g.selectAll(".radarWrapper")
        .data(radarData.statDTOs)
        .enter().append("g")
        .attr("class", "radarWrapper");

      //Init counters to difference between dots and lines from main radar and compare radar
      var linesCounter = 0;
      var classCounter = 0;
      var xCoordCounter = 0;
      var yCoordCounter = 0;
      //Append the backgrounds
      blobWrapper
        .append("path")
        .attr("class", "radarArea")
        .attr("d", function(d,i) {
          linesCounter++;
          if(linesCounter > numberOfRadarStats){
            if(lines[linesCounter - 1]){
              return lines[linesCounter - 1];
            }
          }else if(lines[i])
            return lines[i];

          return radarLine(d);
        })
        .style("fill", function(d,i) { return cfg.color[i]; })
        .style("fill-opacity", cfg.opacityArea)
        .on('mouseover', function (d,i){
          //Dim all blobs
          d3.selectAll(".radarArea")
            .transition().duration(200)
            .style("fill-opacity", 0.1);
          //Bring back the hovered over blob
          d3.select(this)
            .transition().duration(200)
            .style("fill-opacity", 0.7);
        })
        .on('mouseout', function(){
          //Bring back all blobs
          d3.selectAll(".radarArea")
            .transition().duration(200)
            .style("fill-opacity", cfg.opacityArea);
        })
        .transition()
        .duration(duration)
        .attr("d", function(d,i) { return radarLine(d) });

      //Reset lines counter
      linesCounter = 0;
      //Create the outlines
      blobWrapper.append("path")
        .attr("class", (d, i) => {
          let classReturned = "radarStroke" + (i > 0 ? "Compare" : "");
          return classReturned;
        })
        .attr("d", function(d,i) {
          linesCounter++;
          if(linesCounter > numberOfRadarStats){
            if(lines[linesCounter - 1]){
              return lines[linesCounter - 1];
            }
          }else if(lines[i])
            return lines[i];

          return radarLine(d);
        })
        .style("stroke-width", (cfg.strokeWidth * CHART_SIZE_RATIO) + "px")
        .style("stroke", function(d,i) { return cfg.borderColor[i]; })
        .style("fill", "none")
        .style("filter" , "url(#glow)")
        .transition()
        .duration(duration)
        .attr("d", function(d,i) { return radarLine(d) });

      //Append the circles
      blobWrapper.selectAll(".radarCircle")
        .data(function(d,i) { return d; })
        .enter().append("circle")
        .attr("class", d => {
          classCounter++;
          let classReturned = "radarCircle" + (classCounter > numberOfRadarStats ? "Compare" : "");
          return classReturned;
        })
        .attr("r", CHART_SIZE_RATIO * cfg.dotRadius - (isMobileDevice * 0.3))
        .attr("cx", function(d,i){
          xCoordCounter++;
          if(xCoordCounter > numberOfRadarStats){
            if(circles[xCoordCounter - 1]){
              return circles[xCoordCounter - 1][0];
            }
          }else if(circles[i]){
            return circles[i][0];
          }
          return rScale((d.value - d.minValue) / (d.maxValue - d.minValue)) * Math.cos(angleSlice*i - Math.PI/2);
        })
        .attr("cy", function(d,i){
          yCoordCounter++;
          if(yCoordCounter > numberOfRadarStats){
            if(circles[yCoordCounter - 1]){
              return circles[yCoordCounter - 1][1];
            }
          }else if(circles[i]){
            return circles[i][1];
          }
          return rScale((d.value - d.minValue) / (d.maxValue -d.minValue)) * Math.sin(angleSlice*i - Math.PI/2);
        })
        .style("fill", function(d,i,j) { return cfg.borderColor[j]; })
        .style("fill-opacity", 0.8)
        .transition()
        .duration(duration)
        .attr("cx", function(d,i){
          return rScale((d.value - d.minValue) / (d.maxValue - d.minValue)) * Math.cos(angleSlice*i - Math.PI/2);
        })
        .attr("cy", function(d,i){
          return rScale((d.value - d.minValue) / (d.maxValue -d.minValue)) * Math.sin(angleSlice*i - Math.PI/2);
        });

      //Wrapper for the invisible circles on top
      var blobCircleWrapper = g.selectAll(".radarCircleWrapper")
        .data(radarData.statDTOs)
        .enter().append("g")
        .attr("class", "radarCircleWrapper");

      //Append a set of invisible circles on top for the mouseover pop-up
      blobCircleWrapper.selectAll(".radarInvisibleCircle")
        .data(function(d,i) { return d; })
        .enter().append("circle")
        .attr("class", "radarInvisibleCircle")
        .attr("r", cfg.dotRadius * 1.5)
        .attr("cx", function(d,i){ return rScale((d.value - d.minValue) / (d.maxValue - d.minValue)) * Math.cos(angleSlice*i - Math.PI/2); })
        .attr("cy", function(d,i){ return rScale((d.value - d.minValue) / (d.maxValue -d.minValue)) * Math.sin(angleSlice*i - Math.PI/2); })
        .style("fill", "none")
        .style("pointer-events", "all")
        .on('mouseover',function(stat, i) {
          let widthTooltip = 350;
          let lines = Math.ceil(self.measureText(self.$t('statsNaming.description.' + stat.code)) / widthTooltip);
          const statName = self.$t('statsNaming.' + stat.code);
          const statProductInfo = getProductInfoById(stat.productId, self.currentUserLanguage);

          let heightTooltip = (CHART_SIZE * 0.2 + (isMobileDevice * 2.5)) + (lines * 10);

          if(((widthTooltip / 2) + this.getBoundingClientRect().left) >= window.innerWidth){
            widthTooltip = (window.innerWidth - this.getBoundingClientRect().left) * 1.75;
          }else if( (this.getBoundingClientRect().left - (widthTooltip / 2))  <= 0){
            widthTooltip = this.getBoundingClientRect().left * 1.75;
          }

          let tooltipY = event.pageY - (heightTooltip + (isMobileDevice * 5));

          if(tooltipY < 0){
            //Hide bottom arrow
            d3.select("body > .tooltip").attr("class",'tooltip no-arrow');
            tooltipY = event.pageY + 8;
          }else{
            //Set bottom arrow
            d3.select("body > .tooltip").attr("class",'tooltip');
          }

          d3.select("body > .tooltip")
            .transition()
            .duration(200)
            .style("display", "inline-block")
            .style("opacity", 0.95)
            .style("width", widthTooltip + "px")
            .style("height", "auto");

          d3.select("body > .tooltip").html("<b>" + statName + "</b></br>" + statProductInfo.name + "</br>" + self.$t('statsNaming.description.' + stat.code))
            .style("font-size", (cfg.tooltipFontSize * CHART_SIZE_RATIO) + "px")
            .style("padding-top", (10 * CHART_SIZE_RATIO) + "px")
            .style("left", this.getBoundingClientRect().left - widthTooltip/2 + (cfg.dotRadius * 1.5) + "px")
            .style("top", tooltipY + "px");

        })
        .on('mouseout',function() {
          d3.select("body > .tooltip").transition()
            .duration(300)
            .style("display", "none");
        });

        if(svg.node()){
          let font = await loadFonts(this.fontFamily);
          copyAndAppendElement(svg.node(),font.node(),0,0);
        }
    },
    drawImage: function(svg, id, x, y, width, height, img) {
      let imgNode = svg.append("svg:image")
        .attr("id", id)
        .attr("x", x)
        .attr("y", y)
        .attr("width", width)
        .attr("height", height)
        .attr("xlink:href", img);
    },
    /////////////////////////////////////////////////////////
  	/////////////////// Helper Function /////////////////////
  	/////////////////////////////////////////////////////////

  	//Taken from http://bl.ocks.org/mbostock/7555321
  	//Wraps SVG text
  	wrap(text, width, offsetX) {
  	  text.each(function() {
    		var text = d3.select(this),
    			words = text.text().split(/\s+/).reverse(),
    			word,
    			line = [],
    			lineNumber = 0,
    			lineHeight = 1.2, // ems
    			y = text.attr("y"),
    			x = text.attr("x"),
    			dy = parseFloat(text.attr("dy")),
    			tspan = text.text(null).append("tspan").attr("x", x).attr("y", y).attr("dy", dy + "em");

    		while (word = words.pop()) {
    		  line.push(word);
    		  tspan.text(line.join(" "));
    		  if (tspan.node().getComputedTextLength() > width) {
    				line.pop();
    				tspan.text(line.join(" "));
    				line = [word];
    				tspan = text.append("tspan").attr("x", x - offsetX).attr("y", y).attr("dy", ++lineNumber * lineHeight + dy + "em").text(word);
    		  }
    		}
  	  });
  	},//wrap
    /* This function returns the size in pixels of the string */
  	measureText: function(string, fontSize = 10) {
  	  const widths = [0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0.278125,0.278125,0.35625,0.55625,
  			0.55625,0.890625,0.6671875,0.1921875,0.334375,0.334375,0.390625,0.584375,0.278125,0.334375,0.278125,0.303125,0.55625,
  			0.55625,0.55625,0.55625,0.55625,0.55625,0.55625,0.55625,0.55625,0.55625,0.278125,0.278125,0.5859375,0.584375,0.5859375,
  			0.55625,1.015625,0.6671875,0.6671875,0.7234375,0.7234375,0.6671875,0.6109375,0.778125,0.7234375,0.278125,0.5,0.6671875,
  			0.55625,0.834375,0.7234375,0.778125,0.6671875,0.778125,0.7234375,0.6671875,0.6109375,0.7234375,0.6671875,0.9453125,
  			0.6671875,0.6671875,0.6109375,0.278125,0.35625,0.278125,0.478125,0.55625,0.334375,0.55625,0.55625,0.5,0.55625,
  			0.55625,0.278125,0.55625,0.55625,0.2234375,0.2421875,0.5,0.2234375,0.834375,0.55625,0.55625,0.55625,0.55625,
  			0.334375,0.5,0.278125,0.55625,0.5,0.7234375,0.5,0.5,0.5,0.35625,0.2609375,0.3546875,0.590625];
  	  const avg = 0.5293256578947368;

  	  return string
  	    .split('')
  	    .map(c => c.charCodeAt(0) < widths.length ? widths[c.charCodeAt(0)] : avg)
  	    .reduce((cur, acc) => acc + cur) * fontSize;
  	},
    onResize(){
      let elto = document.getElementById(this.rootElementId);
      if(elto){
        this.$emit("sizeChange", {height:elto.clientHeight, width:elto.clientWidth});
      }
    }
  },
  destroyed() {
    d3.select(`#${this.rootElementId}`).select("svg").remove();
    delete this.observer;
  },
}

</script>
<style lang="scss">
</style>



// WEBPACK FOOTER //
// src/views/base/RadarChart.vue